import React from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import * as Icons from "lucide-react";

export const PageHeader = ({ title, subtitle, children, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6" data-testid={testId}>
    <div className="min-w-0">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground truncate">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">{children}</div>}
  </div>
);

export const StatCard = ({ label, value, icon, accent = "text-primary", sub, testId }) => {
  const Icon = Icons[icon] || Icons.Activity;
  return (
    <Card className="p-4 sm:p-5 relative overflow-hidden fp-fade-in" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] sm:text-xs font-medium uppercase tracking-wider text-muted-foreground truncate">{label}</p>
          <p className={cn("font-mono text-xl sm:text-2xl font-bold mt-1.5 sm:mt-2 tracking-tight break-words", accent)}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
        <div className={cn("h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-xl grid place-items-center bg-primary/10", accent)}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </div>
    </Card>
  );
};

export const Loader = ({ label = "Loading" }) => (
  <div className="flex items-center justify-center py-24 text-muted-foreground gap-3">
    <Icons.LoaderCircle className="h-5 w-5 animate-spin" />
    <span className="text-sm">{label}…</span>
  </div>
);

export const EmptyState = ({ icon = "Inbox", title, subtitle }) => {
  const Icon = Icons[icon] || Icons.Inbox;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-2xl bg-muted grid place-items-center mb-4">
        <Icon className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="font-semibold text-foreground">{title}</p>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
    </div>
  );
};

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  inactive: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  open: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  resolved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  within_target: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  above_target: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  requires_review: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

export const StatusPill = ({ status, label, testId }) => (
  <span
    data-testid={testId}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize",
      STATUS_STYLES[status] || "bg-muted text-muted-foreground border-border"
    )}
  >
    {(label || status || "").replace(/_/g, " ")}
  </span>
);

export const Plate = ({ reg, className }) => (
  <span
    className={cn(
      "font-mono font-bold tracking-widest text-sm px-2 py-0.5 rounded bg-amber-400 text-black border border-amber-500",
      className
    )}
  >
    {reg || "—"}
  </span>
);
